import { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Navigate, useNavigate } from 'react-router';
import Swal from 'sweetalert2';
import AppHeader from '../components/AppHeader';
import StatusMessage from '../components/StatusMessage';
import useGuardarToolbox from '../hooks/useGuardarToolbox';
import SignaturePad from '../components/SignaturePad';
import AppPageLayout from '../layout/AppPageLayout';
import { takePhoto } from '../services/cameraService';
import { getToolboxTalkOptions } from '../services/toolboxService';
import { useAuthStore } from '../store/authStore';
import { useToolboxStore } from '../store/toolboxStore';

function ToolboxCargaPage() {
  const navigate = useNavigate();
  const session = useAuthStore((state) => state.session);
  const { guardar, loading, error } = useGuardarToolbox();
  const participantsSeleccionados = useToolboxStore(
    (state) => state.participantsSeleccionados
  );
  const filters = useToolboxStore((state) => state.filters);
  const charlaId = useToolboxStore((state) => state.charlaId);
  const observaciones = useToolboxStore((state) => state.observaciones);
  const evidenciasFoto = useToolboxStore((state) => state.evidenciasFoto);
  const firma = useToolboxStore((state) => state.firma);
  const setCharlaId = useToolboxStore((state) => state.setCharlaId);
  const setObservaciones = useToolboxStore((state) => state.setObservaciones);
  const agregarEvidenciaFoto = useToolboxStore((state) => state.agregarEvidenciaFoto);
  const quitarEvidenciaFoto = useToolboxStore((state) => state.quitarEvidenciaFoto);
  const reemplazarEvidenciaFoto = useToolboxStore((state) => state.reemplazarEvidenciaFoto);
  const setFirma = useToolboxStore((state) => state.setFirma);
  const resetToolbox = useToolboxStore((state) => state.resetToolbox);
  const charlaOptions = getToolboxTalkOptions();
  const [capturandoEvidencia, setCapturandoEvidencia] = useState(false);

  if (!participantsSeleccionados.length) {
    return <Navigate to="/toolbox" replace />;
  }

  const canSubmit = Boolean(charlaId && firma && evidenciasFoto[0]?.file);
  const selectedTalk = charlaOptions.find((option) => option.value === charlaId);
  const evidenciaActual = evidenciasFoto[0] || null;
  const evidenciaBusy = capturandoEvidencia || loading;

  const capturarEvidencia = async (modo = 'agregar') => {
    let dataUrl;
    let file;

    try {
      const photo = await takePhoto();
      dataUrl = photo.dataUrl;
      file = photo.file;
    } catch (captureError) {
      if (captureError?.code === 'NO_PHOTO') {
        return;
      }

      await Swal.fire({
        title: 'Error al capturar la foto',
        text: captureError?.message || 'No se pudo obtener la evidencia.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
        confirmButtonColor: '#2e86b5',
      });
      return;
    }

    if (modo === 'reemplazar' && evidenciaActual) {
      reemplazarEvidenciaFoto(evidenciaActual.id, { dataUrl, file });
      return;
    }

    agregarEvidenciaFoto({ dataUrl, file });
  };

  const handleTomarEvidencia = async () => {
    setCapturandoEvidencia(true);
    try {
      await capturarEvidencia(evidenciaActual ? 'reemplazar' : 'agregar');
    } finally {
      setCapturandoEvidencia(false);
    }
  };

  const handleQuitarEvidencia = () => {
    if (!evidenciaActual) {
      return;
    }
    quitarEvidenciaFoto(evidenciaActual.id);
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: '¿Desea borrar la carga?',
      text: 'Va a perder los datos cargados.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, borrar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#ff5a5f',
      cancelButtonColor: '#7f8b9d',
    });

    if (!result.isConfirmed) {
      return;
    }

    resetToolbox();
    navigate('/dashboard', { replace: true });
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }

    try {
      const result = await guardar({
        participantes: participantsSeleccionados,
        charla: observaciones.trim()
          ? `${selectedTalk?.label || charlaId} - ${observaciones.trim()}`
          : selectedTalk?.label || charlaId,
        horaInicio: filters.startTime,
        horaFin: filters.endTime,
        fecha: filters.date,
        responsableId: session?.user?.id,
        tipoOperativo: filters.operationId,
        lugarOperativo: filters.placeId,
        firma,
        evidenciasFoto,
      });

      if (result.evidenciaError) {
        await Swal.fire({
          title: 'Error al guardar la evidencia',
          text: 'No se pudo subir la foto al servidor. El ToolBox no fue registrado.',
          icon: 'error',
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#2e86b5',
        });
        return;
      }

      await Swal.fire(
        result.firmaError
          ? {
              title: 'ToolBox guardado con advertencia',
              text: 'El ToolBox se registró correctamente, pero hubo un problema al guardar la firma. Por favor, contactá a soporte.',
              icon: 'warning',
              confirmButtonText: 'Aceptar',
              confirmButtonColor: '#6c6cf1',
            }
          : {
              title: 'Datos sincronizados correctamente',
              icon: 'success',
              confirmButtonText: 'Aceptar',
              confirmButtonColor: '#6c6cf1',
            }
      );

      resetToolbox();
      navigate('/reportes', { replace: true });
    } catch (err) {
      return err;
    }
  };

  return (
    <AppPageLayout>
      <AppHeader />

      <div className="mx-auto mt-10 w-full max-w-[760px] lg:mt-12">
        <h1 className="text-center font-login text-[30px] font-bold leading-tight text-[#30343b] sm:text-[36px]">
          ToolBox
        </h1>

        <div className="mt-8 border-t border-[#eceff3] pt-6">
          <button
            type="button"
            onClick={() => navigate('/toolbox')}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full text-[#111111] transition hover:bg-[#f5f7fa]"
            aria-label="Volver"
          >
            <ArrowLeft className="h-5 w-5" strokeWidth={2.8} />
          </button>

          <div className="mt-6 rounded-[22px] border border-[#dfe6ed] bg-white p-5 shadow-[0_16px_40px_rgba(15,23,42,0.08)] sm:p-6">
            <p className="text-center font-login text-[24px] font-bold text-[#30343b]">
              Participantes: {participantsSeleccionados.length}/
              {participantsSeleccionados.length}
            </p>

            <div className="mt-6 space-y-5">
              <div>
                <label
                  htmlFor="toolbox-charla"
                  className="mb-2 block font-login text-[14px] font-bold text-[#30343b]"
                >
                  Charla
                </label>
                <select
                  id="toolbox-charla"
                  value={charlaId}
                  onChange={(event) => setCharlaId(event.target.value)}
                  className="h-12 w-full rounded-[12px] border border-[#cfd7df] bg-white px-4 font-login text-[15px] text-[#374151] outline-none transition focus:border-[#2e86b5]"
                >
                  {charlaOptions.map((option) => (
                    <option key={option.value || 'placeholder'} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="toolbox-observaciones"
                  className="mb-2 block font-login text-[14px] font-bold text-[#30343b]"
                >
                  Observaciones
                </label>
                <textarea
                  id="toolbox-observaciones"
                  value={observaciones}
                  onChange={(event) => setObservaciones(event.target.value)}
                  rows={4}
                  placeholder="Observacion(opcional)"
                  className="w-full rounded-[16px] border border-[#d7dce2] bg-white px-4 py-4 font-login text-[14px] text-[#374151] outline-none transition focus:border-[#2e86b5]"
                />
              </div>

              <div className="rounded-[16px] border border-[#d7dce2] bg-white p-4 shadow-[0_6px_20px_rgba(15,23,42,0.08)] sm:p-5">
                <p className="mb-3 font-login text-[14px] font-bold text-[#30343b]">
                  Evidencia fotográfica
                </p>

                {evidenciaActual ? (
                  <>
                    <img
                      src={evidenciaActual.dataUrl}
                      alt="Evidencia capturada"
                      className="max-h-[200px] w-auto rounded-[12px] border border-[#d7dce2] object-contain"
                    />
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={handleQuitarEvidencia}
                        disabled={evidenciaBusy}
                        className="inline-flex items-center justify-center rounded-[10px] border border-[#d7dce2] bg-white px-4 py-2 font-login text-[14px] font-bold text-[#28323d] transition hover:bg-[#f5f7fa] disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Quitar
                      </button>
                      <button
                        type="button"
                        onClick={handleTomarEvidencia}
                        disabled={evidenciaBusy}
                        className="inline-flex items-center justify-center rounded-[8px] bg-[#2e86b5] px-4 py-2 font-login text-[14px] font-bold text-white transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {capturandoEvidencia ? 'Capturando...' : 'Reemplazar'}
                      </button>
                    </div>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={handleTomarEvidencia}
                    disabled={evidenciaBusy}
                    className="inline-flex items-center justify-center rounded-[8px] bg-[#2e86b5] px-5 py-3 font-login text-[16px] font-bold text-white transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {capturandoEvidencia ? 'Capturando...' : 'Tomar evidencia'}
                  </button>
                )}
              </div>

              <div className="rounded-[16px] border border-[#d7dce2] bg-white p-4 shadow-[0_6px_20px_rgba(15,23,42,0.08)]">
                <SignaturePad value={firma} onChange={setFirma} showClearButton={false} />
              </div>
            </div>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={handleDelete}
                className="w-full rounded-[10px] bg-[#ff5a5f] px-4 py-4 font-login text-[15px] font-bold text-white transition hover:brightness-105"
              >
                Borrar
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit || loading || !session?.user?.id}
                className="w-full rounded-[10px] bg-[#205f8d] px-4 py-4 font-login text-[15px] font-bold text-white transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? 'Cargando ToolBox...' : 'Cargar ToolBox'}
              </button>
            </div>

            <StatusMessage
              loading={loading}
              error={error?.message || error}
              success=""
            />

            {!charlaId ? (
              <p className="mt-4 font-login text-[13px] text-[#c43c2f]">
                Debes seleccionar una charla para continuar.
              </p>
            ) : null}

            {charlaId && !firma ? (
              <p className="mt-2 font-login text-[13px] text-[#c43c2f]">
                Debes firmar antes de cargar el ToolBox.
              </p>
            ) : null}

            {!session?.user?.id ? (
              <p className="mt-2 font-login text-[13px] text-[#c43c2f]">
                Debes iniciar sesion para registrar el ToolBox.
              </p>
            ) : null}
          </div>
        </div>

        <div className="pt-10 text-center font-login text-[10px] text-[#5f6673]">
          <p>Copyright 2022 JPH Lions Todos los derechos reservados</p>
        </div>
      </div>
    </AppPageLayout>
  );
}

export default ToolboxCargaPage;
